import type {
  Alarm,
  AggregateBucket,
  ControllerMode,
  HistoricalQuery,
  HistoricalResponse,
  PricePoint,
  ScheduleEntry,
  ScheduleSource,
  SiteSnapshot,
  SiteTopology,
  TsPoint,
} from "./types";

/**
 * Deterministic-ish synthetic data generator. Each topology gets its own
 * generator so site previews stay coherent across reloads.
 *
 * Goals:
 *  - Realistic shapes: PV solar curve, residential/industrial load profile,
 *    BESS following PID-style controller, prices with morning/evening peaks.
 *  - Adaptive to topology: PV only when topology has a PV smartmeter; BESS
 *    aggregate driven by per-unit synthesised SoC.
 *  - Sign convention matches the canonical SIGN_CONVENTION.md:
 *      grid +ve = import, BESS +ve = charging, controller +ve = charge cmd.
 */

const HOUR = 3_600_000;
const MIN = 60_000;

const seedFor = (key: string) => {
  let h = 2166136261;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
};

function fluctuate(base: number, amplitude: number, freq: number, t: number, phase = 0) {
  return base + Math.sin((t / 1000) * freq + phase) * amplitude;
}

/**
 * The demo fault on the largest site: one BESS unit is OFFLINE with a fixed
 * heartbeat age. Shared between snapshot() (online flag + heartbeat pip) and
 * history() (the unit stops producing samples at now − heartbeat_age, so the
 * availability ribbon and the historical charts tell the same story).
 */
const OFFLINE_DEMO = {
  topologyId: "bess-site-1",
  bessIndex: 3, // BESS-4
  heartbeatAgeS: 9999,
} as const;

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * Deterministic pseudo-noise in [−amp, amp]. Combines a jagged per-sample hash
 * (so consecutive chart points wiggle like real telemetry rather than tracing a
 * smooth textbook curve) with a slow drift term. Pure function of (key, t), so
 * the same window always renders identically across reloads.
 */
function hashNoise(t: number, key: string, amp: number) {
  const s = Math.floor(seedFor(key) * 1e6);
  const frac = (n: number) => {
    const x = Math.sin(n * 12.9898 + s) * 43758.5453;
    return x - Math.floor(x);
  };
  const fast = (frac(t / MIN) - 0.5) * 2; // jagged, ~per-minute
  const slow = Math.sin(t / (41 * MIN) + s); // slow correlated drift
  return amp * (fast * 0.6 + slow * 0.4);
}

/** Italian-ish residential+commercial load curve (kW). */
function loadCurveKW(date: Date, peakKW: number) {
  const h = date.getHours() + date.getMinutes() / 60;
  // Two peaks: 08:00 and 19:00; gentle overnight floor.
  const morning = Math.exp(-Math.pow((h - 8) / 2.6, 2)) * peakKW * 0.85;
  const evening = Math.exp(-Math.pow((h - 19) / 2.4, 2)) * peakKW * 1.0;
  const baseline = peakKW * 0.18;
  return baseline + morning + evening;
}

/** PV curve (kW) — gaussian centered at noon, capped at rated kWp. */
function pvCurveKW(date: Date, ratedKWp: number, cloudiness = 0.0) {
  const h = date.getHours() + date.getMinutes() / 60;
  const arc = Math.exp(-Math.pow((h - 13) / 3.0, 2));
  return Math.max(0, ratedKWp * arc * (1 - cloudiness * 0.5));
}

/** Wholesale price curve EUR/MWh. */
function priceCurveEurMWh(date: Date) {
  const h = date.getHours() + date.getMinutes() / 60;
  const morning = Math.exp(-Math.pow((h - 8) / 2.0, 2)) * 65;
  const evening = Math.exp(-Math.pow((h - 20) / 1.8, 2)) * 110;
  const day_solar_dip = Math.exp(-Math.pow((h - 13) / 1.6, 2)) * -55;
  const overnight = 65;
  return Math.max(20, overnight + morning + evening + day_solar_dip);
}

export class MockSite {
  topology: SiteTopology;
  /** Per-BESS state of charge — evolves over time. */
  private soc: Map<string, number>;
  /** Active controller mode (changes based on schedule). */
  activeMode: ControllerMode = "PeakShaving";
  /** Counter for "today's" energy buckets. */
  private todayBaseline = 0;
  /** Active alarms — mutable so the alarm console can ack/clear. */
  alarms: Alarm[];
  /** The schedule entries shipped with the topology. */
  schedule: ScheduleEntry[];
  /** Default mode applied when no entry covers the current time. Tracked
   *  here so the SchedulePage's DefaultEditor round-trips through the
   *  mock adapter the same way it does through the live TB adapter. */
  scheduleDefault: { mode: ControllerMode; params: Record<string, number> } = {
    mode: "gridZeroControl",
    params: {},
  };
  /** Operator-typed schedule meta (name, description, updatedBy, updatedAt). */
  scheduleMeta: {
    name?: string;
    description?: string;
    updatedBy?: string;
    updatedAt?: number;
  } = {};
  /** Optional manual override. `params` carries the mode-specific knobs
   *  (ConstantPower → `power_W`, PeakShaving → `import_max_W`/`export_max_W`). */
  override?: { mode: ControllerMode; expires_at: number; params?: Record<string, number>; reason?: string };
  /** "Now" override for synthetic frozen demos. */
  nowOverride?: number;
  /**
   * One seeded historic grid-meter dropout window [start, end) in epoch ms
   * — `grid_W` history produces NO samples inside it, so the availability
   * Grid row shows a believable past incident and the Historical chart
   * exercises its gap policy. Seeded per site (seedFor, like the rest of
   * the generator) and anchored to the construction hour: always ~7–22 h
   * in the past, 31–40 min long, 15-min aligned so every bucketed
   * granularity loses whole samples instead of phase-luck partials.
   */
  private gridDropout: { start: number; end: number };

  constructor(topology: SiteTopology) {
    this.topology = topology;
    this.soc = new Map();
    topology.bess.forEach((b, i) => {
      const seed = seedFor(`${topology.id}:${b.id}`);
      this.soc.set(b.id, 30 + seed * 50 + i * 4);
    });
    this.alarms = bootstrapAlarms(topology);
    this.schedule = bootstrapSchedule(topology);
    // Pin "today" baseline to local-midnight; this is what the energy_today
    // integrator measures elapsed hours from.
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    this.todayBaseline = midnight.getTime();
    // Seed the historic grid_W dropout (see field doc above): start
    // 7–22 h before the construction hour on a 15-min boundary, lasting
    // 31–40 min — long enough that a 15-min sample grid loses ≥ 3 points.
    const QUARTER = 15 * MIN;
    const anchor = Math.floor(Date.now() / QUARTER) * QUARTER;
    const startQuartersAgo = 28 + Math.floor(seedFor(`${topology.id}:griddrop`) * 61); // 28..88 = 7h..22h
    const dropStart = anchor - startQuartersAgo * QUARTER;
    const dropMin = 31 + Math.floor(seedFor(`${topology.id}:griddroplen`) * 10); // 31..40 min
    this.gridDropout = { start: dropStart, end: dropStart + dropMin * MIN };
  }

  /** Move synthetic time. */
  advance(_dt_ms: number) {
    // Walk SOC slowly: charging during low price, discharging during peak.
    const now = new Date(this.now());
    const price = priceCurveEurMWh(now);
    const cheap = price < 70;
    this.topology.bess.forEach((b) => {
      const cur = this.soc.get(b.id) ?? 50;
      const drift = cheap ? +0.05 : -0.07;
      const next = Math.max(8, Math.min(99, cur + drift + (Math.random() - 0.5) * 0.2));
      this.soc.set(b.id, next);
    });
  }

  now() { return this.nowOverride ?? Date.now(); }

  /** Synthesise a sliding window of past snapshots without disturbing the
   *  rolling SOC state. */
  recent(samples = 60, intervalMs = 1000): SiteSnapshot[] {
    const out: SiteSnapshot[] = [];
    const realNow = Date.now();
    for (let i = samples - 1; i >= 0; i--) {
      this.nowOverride = realNow - i * intervalMs;
      out.push(this.snapshot());
    }
    this.nowOverride = undefined;
    return out;
  }

  snapshot(): SiteSnapshot {
    const t = this.now();
    const now = new Date(t);
    const topo = this.topology;
    const totalCapacity = topo.bess.reduce((s, b) => s + b.capacity_kWh, 0);

    // Synthetic load: scale by topology size.
    const peakKW = totalCapacity * 0.75;
    const buildingLoadKW = loadCurveKW(now, peakKW);

    // PV (if topology has a pv smartmeter or pv aggregation topic).
    const hasPv = topo.smartmeters.some((m) => m.role === "pv") ||
      Boolean(topo.aggregation.pv_power_W);
    const ratedKWp = hasPv ? totalCapacity * 0.45 : 0;
    const cloudiness = 0.15 + 0.12 * Math.sin(t / (12 * HOUR));
    const pvKW = hasPv ? pvCurveKW(now, ratedKWp, cloudiness) : 0;

    // BESS aggregate — controller in PeakShaving mode caps grid import.
    const naiveGridKW = buildingLoadKW - pvKW;
    const peakCapKW = totalCapacity * 0.4;
    let bessTargetKW = 0;
    if (this.activeMode === "PeakShaving") {
      // Discharge to keep grid below cap.
      bessTargetKW = Math.min(0, peakCapKW - naiveGridKW);
      // Charge when grid would export (PV surplus) up to rate.
      if (naiveGridKW < 0) bessTargetKW = Math.max(0, -naiveGridKW * 0.8);
    } else if (this.activeMode === "gridZeroControl") {
      bessTargetKW = -naiveGridKW;
    } else if (this.activeMode === "ConstantPower") {
      // Respect the operator-set power_W (signed: + charge / − discharge),
      // falling back to a -50 kW discharge demo when no override params
      // are present. Matches the edge ConstantPower sign convention.
      bessTargetKW = (this.override?.params?.power_W ?? -50_000) / 1000;
    } else if (this.activeMode === "OFF") {
      bessTargetKW = 0;
    }
    // Clamp by max charge / discharge.
    const maxCharge = topo.bess.reduce((s, b) => s + b.max_charge_kW, 0);
    const maxDischarge = topo.bess.reduce((s, b) => s + b.max_discharge_kW, 0);
    bessTargetKW = Math.max(-maxDischarge, Math.min(maxCharge, bessTargetKW));

    // Distribute among BESS units proportionally to SOC headroom.
    const perUnit = topo.bess.map((b, i) => {
      const soc = this.soc.get(b.id) ?? 50;
      const online = !(topo.id === OFFLINE_DEMO.topologyId && i === OFFLINE_DEMO.bessIndex); // BESS-4 mock fault (largest site)
      const fluct = fluctuate(0, 0.6, 0.6, t, i);
      const share = online ? soc / 100 : 0;
      return { b, soc, online, fluct, share };
    });
    const totalShare = perUnit.reduce((s, x) => s + (x.online ? x.share : 0), 0) || 1;
    const onlineCount = perUnit.filter((x) => x.online).length;
    const faultCount = perUnit.length - onlineCount;

    let actualBessKW = 0;
    const perUnitSnapshots = perUnit.map((u, i) => {
      const target = u.online ? bessTargetKW * (u.share / totalShare) : 0;
      const actual = target + u.fluct;
      actualBessKW += actual;
      const setpoint = target;
      return {
        id: u.b.id,
        name: u.b.name,
        online: u.online,
        active_W: actual * 1000,
        reactive_VAr: 200 * fluctuate(1, 1.0, 0.3, t, i + 7) * 1000,
        soc_pct: u.soc,
        total_voltage_V: 720 + u.fluct * 4 + i * 5,
        ambient_temperature_C: 24 + 1.2 * Math.sin(t / HOUR + i),
        fault_count: u.online ? 0 : 3,
        fault_summary: u.online ? undefined : "Inverter trip · DC isolation low",
        setpoint_W: setpoint * 1000,
        bess_onoff: u.online && this.activeMode !== "OFF",
        heartbeat_age_s: u.online ? Math.random() * 1.5 : OFFLINE_DEMO.heartbeatAgeS,
      };
    });

    const gridKW = naiveGridKW + actualBessKW; // BESS charge → grid imports more
    const gridReactiveVAr = 24_000 + 2000 * Math.sin(t / (10 * MIN));
    const freq = 50.0 + 0.04 * Math.sin(t / (5 * MIN));
    const voltage = 230.5 + 1.2 * Math.sin(t / (3 * MIN));

    // Fold the curves from midnight to "now" so today-totals are realistic
    // even at the moment the SPA boots.
    const today_pv = hasPv
      ? foldToday({ now: t, baseline: this.todayBaseline, sample: (d) => pvCurveKW(d, ratedKWp, cloudiness) })
      : 0;
    const today_load = foldToday({ now: t, baseline: this.todayBaseline, sample: (d) => loadCurveKW(d, peakKW) });
    // BESS contribution is ±. Integrate the *positive* and *negative* parts
    // independently so today_charge and today_discharge are both meaningful.
    const bessAt = (d: Date) => {
      const ld = loadCurveKW(d, peakKW);
      const pv = hasPv ? pvCurveKW(d, ratedKWp, cloudiness) : 0;
      const naive = ld - pv;
      const cap = totalCapacity * 0.4;
      let target = Math.min(0, cap - naive);
      if (naive < 0) target = Math.max(0, -naive * 0.8);
      return Math.max(-maxDischarge, Math.min(maxCharge, target));
    };
    const today_bess_charge = foldToday({
      now: t, baseline: this.todayBaseline,
      sample: (d) => Math.max(0, bessAt(d)),
    });
    const today_bess_discharge = foldToday({
      now: t, baseline: this.todayBaseline,
      sample: (d) => Math.max(0, -bessAt(d)),
    });
    const today_grid_signed_at = (d: Date) => loadCurveKW(d, peakKW) - (hasPv ? pvCurveKW(d, ratedKWp, cloudiness) : 0) + bessAt(d);
    const today_grid_import = foldToday({ now: t, baseline: this.todayBaseline, sample: (d) => Math.max(0, today_grid_signed_at(d)) });
    const today_grid_export = foldToday({ now: t, baseline: this.todayBaseline, sample: (d) => Math.max(0, -today_grid_signed_at(d)) });

    const totalConsumedKWh = today_pv + today_bess_discharge + today_grid_import - today_grid_export;
    const onSiteKWh = today_pv + today_bess_discharge;
    const selfConsumption = today_pv > 0
      ? Math.min(99, ((today_pv - today_grid_export) / today_pv) * 100)
      : 0;
    const selfSufficiency = totalConsumedKWh > 0
      ? Math.min(99, (onSiteKWh / totalConsumedKWh) * 100)
      : 0;

    // Money saved proxy: BESS arbitrage delta (charged at low price, discharged at
    // high price) + PV self-consumption avoided cost.
    const arbitrage = foldToday({
      now: t, baseline: this.todayBaseline,
      sample: (d) => {
        const bess = bessAt(d);
        const price = priceCurveEurMWh(d) / 1000;
        return -bess * price; // discharge contributes positively
      },
    });
    const pvAvoided = today_pv * (selfConsumption / 100) * 0.16;
    const moneySaved = arbitrage + pvAvoided;

    const co2Avoided = (today_pv + today_bess_discharge) * (topo.prices.carbon_intensity_g_kWh / 1000);

    return {
      ts: t,
      connection: "live",
      grid: {
        active_W: gridKW * 1000,
        reactive_VAr: gridReactiveVAr,
        voltage_V: voltage,
        current_A: Math.abs(gridKW * 1000) / (voltage * Math.sqrt(3)),
        frequency_Hz: freq,
        pf: 0.95 + 0.02 * Math.sin(t / (4 * MIN)),
      },
      bess_aggregate: {
        active_W: actualBessKW * 1000,
        reactive_VAr: perUnitSnapshots.reduce((s, x) => s + x.reactive_VAr, 0),
        soc_avg_pct: perUnitSnapshots.filter((x) => x.online)
          .reduce((s, x) => s + x.soc_pct, 0) / Math.max(1, onlineCount),
        soc_min_pct: Math.min(...perUnitSnapshots.filter((x) => x.online).map((x) => x.soc_pct).concat([100])),
        soc_max_pct: Math.max(...perUnitSnapshots.filter((x) => x.online).map((x) => x.soc_pct).concat([0])),
        online_count: onlineCount,
        fault_count: faultCount,
        total_capacity_kWh: totalCapacity,
        total_voltage_V: perUnitSnapshots[0]?.total_voltage_V ?? 0,
        avg_temperature_C: perUnitSnapshots.reduce((s, x) => s + x.ambient_temperature_C, 0) /
          Math.max(1, perUnitSnapshots.length),
      },
      bess_per_unit: perUnitSnapshots,
      pv: hasPv ? { active_W: pvKW * 1000, today_kWh: today_pv, rated_kWp: ratedKWp } : undefined,
      // NET building load (gross load − PV): matches the "Net building load"
      // label and live semantics — negative when PV surplus exceeds load.
      building_load_W: naiveGridKW * 1000,
      net_at_meter_W: gridKW * 1000,
      controller: {
        active_mode: this.activeMode,
        auto_bess_on: this.activeMode !== "OFF",
        schedule_source: this.override ? "manual_override" : "cloud",
        override_expires_at: this.override?.expires_at,
        pid_setpoint_W: bessTargetKW * 1000,
        pid_actively_controlling: this.activeMode !== "OFF" && Math.abs(actualBessKW) > 0.5,
        health: faultCount > 0 ? "warn" : "ok",
      },
      health: {
        overall: this.alarms.some((a) => a.severity === "critical" && a.status === "active")
          ? "critical"
          : this.alarms.some((a) => a.status === "active")
            ? "warn"
            : "ok",
        services_active: 7,
        services_total: 8,
        cloud_uplink: "live",
        last_uplink_age_s: 2 + Math.random() * 3,
      },
      energy_today: {
        grid_import_kWh: today_grid_import,
        grid_export_kWh: today_grid_export,
        pv_kWh: today_pv,
        bess_charge_kWh: today_bess_charge,
        bess_discharge_kWh: today_bess_discharge,
        self_consumption_pct: selfConsumption,
        self_sufficiency_pct: selfSufficiency,
        money_saved_eur: moneySaved,
        co2_avoided_kg: co2Avoided,
      },
    };
  }

  /**
   * Synthesize a realistic historical timeseries by replaying the daily
   * load / PV / price shapes across the requested window. Adds per-day weather
   * + weekday/weekend variation, fast PV cloud-shadow transients, SOC-aware
   * BESS dispatch (soak PV surplus, charge cheap overnight, discharge into the
   * evening peak), and jagged per-sample noise — so the charts read like real
   * telemetry rather than smooth textbook curves. Deterministic per window.
   */
  history(query: HistoricalQuery): HistoricalResponse {
    const granularityMs: Record<HistoricalQuery["granularity"], number> = {
      raw: 15_000, // finer raw cadence for denser inspect charts
      "2m": 2 * MIN,
      "5m": 5 * MIN,
      "15m": 15 * MIN,
      h: HOUR,
      d: 24 * HOUR,
      w: 7 * 24 * HOUR,
      m: 30 * 24 * HOUR,
      y: 365 * 24 * HOUR,
    };
    const step = granularityMs[query.granularity];
    // Cap synthesized points so a wide window at a fine granularity (e.g. 30 d
    // at raw=15 s ≈ 173k points/key) can't freeze the tab — coarsen the step to
    // keep the loop bounded. Dense short windows are unaffected (small span →
    // effStep === step). Protects every caller incl. the demo fallback.
    const MAX_PTS = 8000;
    const span = Math.max(0, query.end_ts - query.start_ts);
    const effStep = Math.max(step, Math.ceil(span / MAX_PTS) || step);
    const series: Record<string, TsPoint[]> = {};
    for (const k of query.keys) series[k] = [];

    const topo = this.topology;
    const totalCapacity = topo.bess.reduce((s, b) => s + b.capacity_kWh, 0) || 200;
    const peakKW = totalCapacity * 0.75;
    const hasPv = topo.smartmeters.some((m) => m.role === "pv");
    const ratedKWp = hasPv ? totalCapacity * 0.55 : 0;
    const maxCharge = topo.bess.reduce((s, b) => s + b.max_charge_kW, 0) || 100;
    const maxDischarge = topo.bess.reduce((s, b) => s + b.max_discharge_kW, 0) || 100;
    const nUnits = Math.max(1, topo.bess.length);

    // Integrate SOC forward across the window so the SOC trace cycles
    // realistically instead of being a free-running sine.
    let soc = 50 + seedFor(`${topo.id}:hist`) * 20;

    // --- History honesty (availability ribbons + gap-aware charts) ----
    // The demo OFFLINE unit stops producing samples at (now − heartbeat
    // age): both its per-BESS catalog queries (`bess_id` + soc/…) and its
    // device-keyed series go silent, matching the live snapshot's
    // online=false + heartbeat pip. The grid meter additionally has one
    // seeded historic dropout window (this.gridDropout) on `grid_W`.
    // All other series are untouched.
    const offlineBessId =
      topo.id === OFFLINE_DEMO.topologyId ? topo.bess[OFFLINE_DEMO.bessIndex]?.id : undefined;
    const offlineSince = this.now() - OFFLINE_DEMO.heartbeatAgeS * 1000;
    const offlineUnitQuery = offlineBessId !== undefined && query.bess_id === offlineBessId;
    const offlineDevicePrefix =
      offlineBessId !== undefined ? `device:mock-bess-${offlineBessId}:` : null;
    const blackout = (k: string, ts: number): boolean => {
      if (k === "grid_W" && ts >= this.gridDropout.start && ts < this.gridDropout.end) return true;
      if (ts < offlineSince) return false;
      if (offlineUnitQuery && !k.startsWith("device:")) return true;
      return offlineDevicePrefix !== null && k.startsWith(offlineDevicePrefix);
    };

    for (let t = query.start_ts; t <= query.end_ts; t += effStep) {
      const date = new Date(t);
      const h = date.getHours() + date.getMinutes() / 60;
      const dow = date.getDay();
      const weekend = dow === 0 || dow === 6;
      const dayIdx = Math.floor(t / (24 * HOUR));

      // Per-day character (deterministic): load scale + weather/cloudiness.
      const dayScale = (weekend ? 0.68 : 0.92) + seedFor(`load:${dayIdx}`) * 0.22;
      const cloudiness = 0.08 + seedFor(`wx:${dayIdx}`) * 0.45;

      // Load — double-peak shape × per-day scale + jagged noise, floored.
      const buildingLoadKW = Math.max(
        peakKW * 0.12,
        loadCurveKW(date, peakKW) * dayScale + hashNoise(t, "load", peakKW * 0.05),
      );

      // PV — solar bell × (1 − cloudiness) with brief cloud-shadow dips.
      let pvKW = 0;
      if (hasPv) {
        const shadow = clamp(hashNoise(t, "cloud", 0.6) + 0.25, 0, 0.85);
        pvKW = Math.max(0, pvCurveKW(date, ratedKWp, cloudiness) * (1 - shadow));
      }

      const price = priceCurveEurMWh(date);
      const naiveGridKW = buildingLoadKW - pvKW;

      // BESS dispatch (+ charge / − discharge), SOC- and power-limited.
      let bessKW = 0;
      if (naiveGridKW < -1) {
        bessKW = Math.min(maxCharge, -naiveGridKW * 0.92); // PV surplus → charge
      } else if ((h >= 18 && h < 22) || price > 120) {
        bessKW = -Math.min(maxDischarge, naiveGridKW * 0.6); // evening peak → discharge
      } else if (price < 75 && (h < 6 || h >= 23)) {
        bessKW = Math.min(maxCharge, peakKW * 0.3); // cheap night → charge
      }
      if (bessKW < 0 && soc <= 12) bessKW = 0;
      if (bessKW > 0 && soc >= 96) bessKW = 0;
      bessKW += hashNoise(t, "bess", Math.max(1.5, Math.abs(bessKW) * 0.05));

      soc = clamp(soc + (bessKW * (effStep / HOUR) / totalCapacity) * 100, 8, 99);

      const gridKW = naiveGridKW + bessKW + hashNoise(t, "grid", 1.8);

      for (const k of query.keys) {
        // A blacked-out (key, ts) emits NO sample — a real hole in the
        // series, exactly like a silent device. SOC integration above is
        // per-timestep (outside this loop), so skipping a key is safe.
        if (blackout(k, t)) continue;
        let v = 0;
        switch (k) {
          case "grid_W": v = gridKW * 1000; break;
          case "battery/aggregation/power":
          case "ac_active_power": v = bessKW * 1000; break;
          case "pv_power_W": v = pvKW * 1000; break;
          // NET building load (gross consumption − on-site PV) to match the
          // "Net building load" label + the live key semantics: dips negative
          // when PV surplus exceeds load. naiveGridKW === buildingLoadKW − pvKW.
          case "building_load_W": v = naiveGridKW * 1000; break;
          case "soc":
          case "battery/aggregation/SOC": v = soc; break;
          case "price_eur_mwh": v = price; break;
          case "ambient_temperature":
            v = 22 + 5 * Math.sin(((h - 15) / 24) * 2 * Math.PI) + hashNoise(t, "temp", 0.8); break;
          default: {
            // Mock device-keyed routing: `device:mock-grid:active_W`,
            // `device:mock-bess-<id>:soc_pct`, etc.
            if (k.startsWith("device:")) {
              const [, devId, ...rest] = k.split(":");
              const raw = rest.join(":");
              if (devId === "mock-grid" && raw === "active_W")      v = gridKW * 1000;
              else if (devId === "mock-grid" && raw === "reactive_VAr") v = gridKW * 180 + hashNoise(t, k, 400);
              else if (devId.startsWith("mock-bess-") && raw === "active_W")   v = (bessKW * 1000) / nUnits + hashNoise(t, k, 300);
              else if (devId.startsWith("mock-bess-") && raw === "soc_pct")    v = clamp(soc + seedFor(devId) * 6 - 3, 8, 99);
              else if (devId.startsWith("mock-bess-") && raw === "total_voltage_V") v = 720 + 8 * Math.sin(t / HOUR + seedFor(devId)) + hashNoise(t, k, 1.5);
              else if (devId.startsWith("mock-bess-") && raw === "ambient_temperature_C") v = 23 + 4 * Math.sin(t / (24 * HOUR) + seedFor(devId)) + hashNoise(t, k, 0.6);
              else v = hashNoise(t, k, 50);
            } else {
              v = hashNoise(t, k, 100);
            }
          }
        }
        series[k].push({ ts: t, value: v });
      }
    }
    return { series };
  }

  /** Rolling 24-hour day-ahead price curve. */
  priceCurve(start_ts: number, hours = 48): PricePoint[] {
    const out: PricePoint[] = [];
    for (let i = 0; i < hours; i++) {
      const ts = start_ts + i * HOUR;
      out.push({ ts, price: priceCurveEurMWh(new Date(ts)) });
    }
    return out;
  }

  /** Aggregated buckets — for revenue charts. */
  buckets(start_ts: number, end_ts: number, gran: "h" | "d" | "w" | "m"): AggregateBucket[] {
    const step = ({ h: HOUR, d: 24 * HOUR, w: 7 * 24 * HOUR, m: 30 * 24 * HOUR } as const)[gran];
    const out: AggregateBucket[] = [];
    for (let t = start_ts; t < end_ts; t += step) {
      const d = new Date(t);
      const peakKW = this.topology.bess.reduce((s, b) => s + b.capacity_kWh, 0) * 0.75;
      const ep = loadCurveKW(d, peakKW) * (step / HOUR) * 0.55;
      const hasPv = this.topology.smartmeters.some((m) => m.role === "pv");
      const en = hasPv ? pvCurveKW(d, peakKW * 0.6) * (step / HOUR) * 0.65 : 0;
      const price = priceCurveEurMWh(d) / 1000;
      out.push({
        start: t,
        ep_kWh: ep,
        en_kWh: en,
        net_kWh: ep - en,
        cost_eur: ep * price,
        revenue_eur: en * price * 0.6,
      });
    }
    return out;
  }

  scheduleSource(): ScheduleSource {
    const now = new Date(this.now());
    const nowMin = now.getHours() * 60 + now.getMinutes();
    const dow = (now.getDay() + 6) % 7;
    const ymd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const candidates = this.schedule
      .filter((s) => s.enabled && s.days_of_week.includes(dow))
      .filter((s) => !s.date_start || ymd >= s.date_start)
      .filter((s) => !s.date_end || ymd <= s.date_end)
      .filter((s) => {
        if (s.start_min === s.end_min) return false;
        const inWrap = s.start_min > s.end_min;
        return inWrap
          ? nowMin >= s.start_min || nowMin < s.end_min
          : nowMin >= s.start_min && nowMin < s.end_min;
      })
      .sort((a, b) => (b.priority - a.priority) || a.id.localeCompare(b.id));
    const active = candidates[0];
    let next: { at: number; mode: ControllerMode } | undefined;
    if (active) {
      const todayMid = new Date(this.now()).setHours(0, 0, 0, 0);
      next = { at: todayMid + active.end_min * 60_000, mode: this.scheduleDefault.mode };
    }
    const doc = {
      version: 1 as const,
      timezone: this.topology.timezone,
      default: this.scheduleDefault,
      entries: this.schedule,
      meta: this.scheduleMeta,
    };
    return {
      source: this.override ? "manual_override" : "cloud",
      doc,
      // A manual override beats the schedule, so `active_now` reflects
      // the override mode — same as the live tbAdapter.schedule().
      active_now: this.override
        ? { mode: this.override.mode }
        : { entry: active, mode: active?.mode ?? this.scheduleDefault.mode },
      next_change: next,
      override: this.override,
    };
  }
}

/**
 * Replay-style "today integral" — fold the curve from midnight to now in
 * 30-minute slabs so today's totals reflect the realistic intra-day shape
 * (PV around noon, evening peak load, etc.) rather than a flat extrapolation.
 *
 * The `kind` toggles which underlying curve we want to integrate. We use a
 * function-shaped argument instead of a single `kw` because the live snapshot
 * already represents *now* — for the today total we need to integrate the
 * historical shape since midnight.
 */
function integrateToday(now: number, todayBaseline: number, _kwNow: number, _factor: number, _peakAdj: number) {
  // Conservative fall-back; the explicit per-kind helper below is preferred.
  return Math.max(0, _kwNow * ((now - todayBaseline) / HOUR) * _factor * _peakAdj);
}

function foldToday<T extends { now: number; baseline: number; sample: (d: Date) => number; floor?: number }>(args: T) {
  const stepMs = 30 * 60_000;
  let total = 0;
  for (let t = args.baseline; t < args.now; t += stepMs) {
    const v = Math.max(args.floor ?? 0, args.sample(new Date(t)));
    total += v * (stepMs / HOUR);
  }
  return total;
}

function bootstrapAlarms(topo: SiteTopology): Alarm[] {
  const now = Date.now();
  const out: Alarm[] = [];
  // Multi-BESS site (Site 1): a richer alarm mix for the demo.
  if (topo.bess.length >= 4) {
    out.push({
      id: "alm-s1-1",
      device_id: "bess-4",
      device_name: "BESS-4",
      rule_type: "boolean",
      source_topic: "battery/measurement/4/fault_inverter_trip",
      severity: "critical",
      status: "active",
      message: "Inverter trip — DC isolation low",
      detail: "BESS-4 inverter latched off after a DC isolation alarm. Manual reset required at the cabinet after isolation check.",
      raised_at: now - 47 * MIN,
      comments: [],
    });
    out.push({
      id: "alm-s1-2",
      device_id: "bess-2",
      device_name: "BESS-2",
      rule_type: "threshold",
      source_topic: "battery/measurement/2/SOC",
      severity: "minor",
      status: "active",
      message: "SOC over 95%",
      detail: "BESS-2 reached 96% SOC and absorption is being limited. Consider scheduling a discharge.",
      raised_at: now - 9 * MIN,
      comments: [],
    });
    out.push({
      id: "alm-s1-3",
      device_id: "sm-grid",
      device_name: "Grid coupling",
      rule_type: "inactivity",
      source_topic: "smartmeter/measurement/2/power_W",
      severity: "warning",
      status: "active",
      message: "Grid meter inactive 25 min",
      detail: "The grid coupling meter has not refreshed in 25 minutes — investigate the Modbus bridge.",
      raised_at: now - 25 * MIN,
      comments: [],
    });
    // Action-shaped alarm (N25): a peak-demand warning on the grid-coupling
    // meter so the "Adjust dispatch →" lever (which deep-links to a
    // PeakShaving schedule draft) is visible in the demo. The grid device
    // also resolves to the "View meter" drill (N23).
    out.push({
      id: "alm-s1-4",
      device_id: "sm-grid",
      device_name: "Grid coupling",
      rule_type: "peak_demand",
      source_topic: "smartmeter/measurement/2/power_W",
      severity: "warning",
      status: "active",
      message: "Approaching contract peak demand",
      detail: "Grid import is trending toward the contracted peak for this billing window. Consider a PeakShaving window on the schedule to cap the demand charge.",
      raised_at: now - 6 * MIN,
      comments: [],
    });
  } else {
    out.push({
      id: "alm-s2-1",
      device_id: "bess-1",
      device_name: "BESS-1",
      rule_type: "stuck",
      source_topic: "battery/measurement/1/SOC",
      severity: "minor",
      status: "active",
      message: "SOC sensor steady — possible stuck reading",
      detail: "SOC has not changed by more than 0.05 pp in 22 min. Could be normal idle or a stuck telemetry frame.",
      raised_at: now - 22 * MIN,
      comments: [],
    });
  }
  return out;
}

function bootstrapSchedule(_topo: SiteTopology): ScheduleEntry[] {
  return [
    {
      id: "default-day",
      name: "Daytime peak shaving",
      enabled: true,
      priority: 5,
      days_of_week: [0, 1, 2, 3, 4],
      start_min: 7 * 60,
      end_min: 21 * 60,
      mode: "PeakShaving",
      params: { import_max_W: 60_000, export_max_W: 50_000 },
    },
    {
      id: "default-night",
      name: "Overnight grid zero",
      enabled: true,
      priority: 4,
      days_of_week: [0, 1, 2, 3, 4, 5, 6],
      start_min: 21 * 60,
      end_min: 7 * 60,
      mode: "gridZeroControl",
      params: {},
    },
    {
      id: "weekend",
      name: "Weekend baseline",
      enabled: true,
      priority: 3,
      days_of_week: [5, 6],
      start_min: 7 * 60,
      end_min: 21 * 60,
      mode: "gridZeroControl",
    },
  ];
}
